import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Badge, CollapsibleSection, Zap } from '@opendate-app/setlist';
import './PriceBreakoutAccordion.css';

const currencyFormatter = (currency) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency,
});

const BreakoutColumns = ({showCash}) => (
  <colgroup>
    <col style={{width: showCash ? "26%" : "34%"}} />
    <col style={{width: "40%"}} />
    {showCash ? <col style={{width: "20%"}} /> : null}
    <col style={{width: "20%"}} />
    <col style={{width: "6%"}} />
  </colgroup>
);

const DynamicRange = ({min, max}) => (
  min && max ? (
    <Badge color="brand" size="sm" icon={Zap}>
      ${min.toFixed(2)}-${max.toFixed(2)}
    </Badge>
  ) : null
);

const FinalPriceSummary = ({item, dynamicPricingInfo, showCash, formatter}) => (
  <span className="price-breakout-summary">
    {showCash ? (
      <span className="price-breakout-summary-value">
        <span>
          <strong>Cash</strong>:{" "}
          {formatter.format(parseFloat(item.cash_final_cost) + parseFloat(item.cash_taxes))}
        </span>
        <DynamicRange min={dynamicPricingInfo?.cashMin} max={dynamicPricingInfo?.cashMax} />
      </span>
    ) : null}
    <span className="price-breakout-summary-value">
      <span>
        <strong>Card</strong>:{" "}
        {formatter.format(parseFloat(item.final_cost) + parseFloat(item.taxes))}
      </span>
      <DynamicRange min={dynamicPricingInfo?.cardMin} max={dynamicPricingInfo?.cardMax} />
    </span>
  </span>
);

const BreakoutRow = ({label, note, cash, card, showCash, ruled = false}) => (
  <tr className={ruled ? "price-breakout-row--ruled" : undefined}>
    <td><strong>{label}</strong></td>
    <td className="price-breakout-note">{note}</td>
    {showCash ? <td className="price-breakout-amount">{cash}</td> : null}
    <td className="price-breakout-amount">{card}</td>
    <td>&nbsp;</td>
  </tr>
);

const PriceBreakoutAccordion = ({item, accordionId = "Main", dynamicPricingInfo, defaultOpen = false, showCash = true, currency = 'USD'}) => {
  const [open, setOpen] = useState(defaultOpen);
  const formatter = currencyFormatter(currency);
  const money = (value) => formatter.format(parseFloat(value));
  const absorbed = item.absorb_fees ? "absorbed" : null;
  const basePrice = item.base_price_two_decimals ? item.base_price_two_decimals : 0.0;

  if (!(item.final_cost && item.final_cost !== "0.0" && item.final_cost !== 0.0)) {
    return null;
  }

  return (
    <div className="price-breakout" id={`accordion${accordionId}`}>
      <CollapsibleSection
        size="sm"
        open={open}
        onOpenChange={setOpen}
        title="Final Price"
        meta={!open ? (
          <FinalPriceSummary item={item} dynamicPricingInfo={dynamicPricingInfo} showCash={showCash} formatter={formatter} />
        ) : null}
      >
        <div id={`collapse${accordionId}`}>
          <table className="price-breakout-table">
            <BreakoutColumns showCash={showCash} />
            <thead>
              <tr>
                <th scope="col">&nbsp;</th>
                <th scope="col">&nbsp;</th>
                {showCash ? <th scope="col" className="price-breakout-amount">Cash</th> : null}
                <th scope="col" className="price-breakout-amount">Card</th>
                <th scope="col"></th>
              </tr>
            </thead>
            <tbody>
              <BreakoutRow showCash={showCash} label="Base Price:" cash={money(basePrice)} card={money(basePrice)} />
              {dynamicPricingInfo && (dynamicPricingInfo.cashMin || dynamicPricingInfo.cardMin) ? (
                <BreakoutRow
                  showCash={showCash}
                  label={(
                    <span className="price-breakout-dynamic-label">
                      <Zap width={12} height={12} aria-hidden="true" />
                      <span>Dynamic Pricing Range:</span>
                    </span>
                  )}
                  cash={dynamicPricingInfo.cashMin && dynamicPricingInfo.cashMax ? (
                    <span className="price-breakout-dynamic-range">
                      {formatter.format(dynamicPricingInfo.cashMin)} - {formatter.format(dynamicPricingInfo.cashMax)}
                    </span>
                  ) : null}
                  card={dynamicPricingInfo.cardMin && dynamicPricingInfo.cardMax ? (
                    <span className="price-breakout-dynamic-range">
                      {formatter.format(dynamicPricingInfo.cardMin)} - {formatter.format(dynamicPricingInfo.cardMax)}
                    </span>
                  ) : null}
                />
              ) : null}
              {Object.keys(item.custom_charges || {}).map(key => (
                <BreakoutRow
                  showCash={showCash}
                  key={`custom-charge-${key}`}
                  label={`${key}:`}
                  cash={money(item.cash_custom_charges[key])}
                  card={money(item.custom_charges[key])}
                />
              ))}
              <BreakoutRow showCash={showCash} label="Opendate Fee:" note={absorbed}
                cash={money(item.cash_opendate_service_fee)} card={money(item.opendate_service_fee)} />
              <BreakoutRow showCash={showCash} label="Organizer Fee:" note={absorbed}
                cash={formatter.format(parseFloat(item.cash_organizer_service_fee) - parseFloat(item.cash_round_up_organizer_fee))}
                card={formatter.format(parseFloat(item.organizer_service_fee) - parseFloat(item.round_up_organizer_fee))} />
              {parseFloat(item.rebate_fee) > 0 || parseFloat(item.cash_rebate_fee) > 0 ? (
                <BreakoutRow showCash={showCash} label="Rebate Fee:" note={absorbed}
                  cash={money(item.cash_rebate_fee)} card={money(item.rebate_fee)} />
              ) : null}
              <BreakoutRow showCash={showCash} label="Processing Fee:" note={absorbed}
                cash={money(item.cash_processing_fee)} card={money(item.processing_fee)} />
              {item.round_up && (item.round_up_organizer_fee > 0 || item.cash_round_up_organizer_fee > 0) ? (
                <BreakoutRow showCash={showCash} label="Round Up Fee:"
                  cash={money(item.cash_round_up_organizer_fee)} card={money(item.round_up_organizer_fee)} />
              ) : null}
              <BreakoutRow showCash={showCash} ruled label="Subtotal:"
                cash={money(item.cash_final_cost)} card={money(item.final_cost)} />
              <BreakoutRow showCash={showCash} label="Taxes:"
                cash={money(item.cash_taxes)} card={money(item.taxes)} />
              <BreakoutRow showCash={showCash} ruled label="Total:"
                cash={formatter.format(parseFloat(item.cash_final_cost) + parseFloat(item.cash_taxes))}
                card={formatter.format(parseFloat(item.final_cost) + parseFloat(item.taxes))} />
            </tbody>
          </table>
        </div>
      </CollapsibleSection>
    </div>
  );
};

BreakoutColumns.propTypes = {
  showCash: PropTypes.bool.isRequired,
};

DynamicRange.propTypes = {
  min: PropTypes.number,
  max: PropTypes.number,
};

FinalPriceSummary.propTypes = {
  item: PropTypes.object.isRequired,
  dynamicPricingInfo: PropTypes.object,
  showCash: PropTypes.bool.isRequired,
  formatter: PropTypes.object.isRequired,
};

BreakoutRow.propTypes = {
  label: PropTypes.node.isRequired,
  note: PropTypes.node,
  cash: PropTypes.node,
  card: PropTypes.node,
  showCash: PropTypes.bool.isRequired,
  ruled: PropTypes.bool,
};

PriceBreakoutAccordion.propTypes = {
  item: PropTypes.object.isRequired,
  accordionId: PropTypes.string,
  dynamicPricingInfo: PropTypes.object,
  defaultOpen: PropTypes.bool,
  showCash: PropTypes.bool,
  currency: PropTypes.string,
};

export default PriceBreakoutAccordion;
